import React from "react";
import {
  FaGem,
  FaShieldAlt,
  FaBalanceScale,
  FaBolt,
  FaUserCheck,
  FaMoneyCheckAlt,
} from "react-icons/fa";

const icons = [
  FaGem,
  FaShieldAlt,
  FaBalanceScale,
  FaBolt,
  FaUserCheck,
  FaMoneyCheckAlt,
];

export default function LocationReasonsTimeline({ title, items = [] }) {
  if (!title || items.length === 0) return null;

  return (
    <section className="relative overflow-hidden bg-[#fff8e8] py-14 sm:py-16 lg:py-24">
      {/* Background */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.96),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(215,165,31,0.14),transparent_38%)]" />
      <div className="pointer-events-none absolute left-[-220px] top-[-180px] h-[520px] w-[520px] rounded-full bg-[#f2d36a]/18 blur-3xl" />
      <div className="pointer-events-none absolute right-[-260px] bottom-[-240px] h-[620px] w-[620px] rounded-full bg-[#d7a51f]/12 blur-3xl" />

      <div className="relative mx-auto max-w-[1280px] px-5 sm:px-6 lg:px-10">
        {/* Heading */}
        <div className="mx-auto max-w-[900px] text-center">
          <div className="mx-auto mb-5 flex w-[170px] items-center justify-center gap-4">
            <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#d7a51f]" />
            <span className="h-2.5 w-2.5 rotate-45 bg-[#d7a51f]" />
            <span className="h-px flex-1 bg-gradient-to-r from-[#d7a51f] to-transparent" />
          </div>

          <h2 className="text-[30px] font-extrabold leading-tight tracking-[-0.04em] text-[#071426] sm:text-[42px] lg:text-[54px]">
            {title}
          </h2>
        </div>

        {/* Timeline */}
        <div className="relative mx-auto mt-14 max-w-[1080px] lg:mt-16">
          {/* Center vertical line */}
          <div className="absolute left-6 top-0 h-full w-px bg-gradient-to-b from-[#d7a51f] via-[#ead8a8] to-[#d7a51f] lg:left-1/2 lg:-translate-x-1/2" />

          {/* Top G1 badge */}
          <div className="relative mb-10 flex items-center lg:justify-center">
            <div className="relative z-10 ml-[6px] flex h-12 w-12 items-center justify-center rounded-full border border-[#ead8a8] bg-white shadow-[0_12px_28px_rgba(20,33,61,0.08)] lg:ml-0">
              <span className="text-center text-[10px] font-extrabold leading-tight text-[#d7a51f]">
                G1
                <br />
                GOLD
              </span>
            </div>
          </div>

          <div className="space-y-8 lg:space-y-10">
            {items.map((item, index) => {
              const Icon = icons[index] || FaGem;
              const isLeft = index % 2 === 0;

              return (
                <div
                  key={index}
                  className="relative grid items-center gap-6 pl-16 lg:grid-cols-[1fr_80px_1fr] lg:pl-0"
                >
                  {/* Left side desktop */}
                  <div className={isLeft ? "lg:pr-8" : "hidden lg:block"}>
                    {isLeft && (
                      <TimelineCard
                        item={item}
                        Icon={Icon}
                        index={index}
                        align="left"
                      />
                    )}
                  </div>

                  {/* Center number */}
                  <div className="absolute left-0 top-1 lg:relative lg:left-auto lg:top-auto lg:flex lg:items-center lg:justify-center">
                    <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#d7a51f] via-[#b98210] to-[#6f4507] text-[13px] font-extrabold text-white shadow-[0_14px_32px_rgba(185,130,16,0.28)]">
                      {index + 1}
                    </div>

                    <span
                      className={`absolute top-1/2 hidden h-px w-10 -translate-y-1/2 bg-[#d7a51f] lg:block ${
                        isLeft ? "right-[50px]" : "left-[50px]"
                      }`}
                    />
                  </div>

                  {/* Right side desktop / mobile all cards */}
                  <div className={!isLeft ? "lg:pl-8" : "lg:hidden"}>
                    <TimelineCard
                      item={item}
                      Icon={Icon}
                      index={index}
                      align={isLeft ? "mobile" : "right"}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom diamond */}
          <div className="relative mt-10 flex items-center lg:justify-center">
            <span className="relative z-10 ml-[18px] h-3 w-3 rotate-45 bg-[#d7a51f] lg:ml-0" />
          </div>
        </div>
      </div>
    </section>
  );
}

function TimelineCard({ item, Icon, index, align }) {
  return (
    <div
      className={`group relative overflow-hidden rounded-[18px] border border-[#ead8a8] bg-white px-5 py-5 shadow-[0_18px_50px_rgba(20,33,61,0.08)] transition duration-300 hover:-translate-y-1 hover:border-[#d7a51f] hover:shadow-[0_24px_70px_rgba(20,33,61,0.12)] sm:px-6 sm:py-6 ${
        align === "left" ? "lg:ml-auto" : ""
      }`}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white via-[#fff8e8]/70 to-transparent" />
      <div className="pointer-events-none absolute -right-12 -top-12 h-28 w-28 rounded-full bg-[#d7a51f]/12 blur-2xl" />

      <div className="relative flex items-start gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#ead8a8] bg-[#fff8e8] shadow-[0_10px_24px_rgba(215,165,31,0.10)]">
          <Icon className="text-[21px] text-[#b98210]" />
        </div>

        <div>
          <h3 className="text-[18px] font-extrabold leading-tight text-[#071426] sm:text-[20px]">
            {item.title}
          </h3>

          <p className="mt-2 text-[14px] font-medium leading-6 text-[#334155] sm:text-[15px]">
            {item.text}
          </p>
        </div>
      </div>
    </div>
  );
}